/**
 * Affiliate Distribution Utility
 * 
 * Distributes affiliate slots across networks based on configurable ratios.
 * Ensures fair representation across different slot counts (mobile: 2, desktop: 3, future: 4+).
 * 
 * @example
 * // 2:1 ratio (Amazon:iHerb) for 3 slots → [Amazon, iHerb, Amazon]
 * // 2:1 ratio for 6 slots → [Amazon, iHerb, Amazon, Amazon, iHerb, Amazon]
 */

export type AffiliateNetwork = 'amazon' | 'coupang' | 'iherb' | 'yesstyle';

export interface NetworkConfig {
  name: AffiliateNetwork;
  site: string;
  commission: number;
  /** Weight in the distribution (higher = more slots) */
  weight: number;
  /** Whether this network is configured (has affiliate code) */
  isConfigured: boolean;
}

export interface DistributionConfig {
  /** Primary network (Amazon for US, Coupang for KR) */
  primary: NetworkConfig;
  /** Secondary networks in priority order */
  secondary: NetworkConfig[];
  /** Total number of slots to fill */
  totalSlots: number;
}

/**
 * Distributes slots across networks based on weights.
 * Uses a weighted round-robin approach to ensure fair distribution
 * that works regardless of total slot count.
 * 
 * Algorithm:
 * 1. Calculate total weight of configured networks
 * 2. For each slot, determine which network based on weighted position
 * 3. Primary network gets floor(slots * primaryWeight / totalWeight) slots minimum
 * 4. Remaining slots distributed to secondary networks proportionally
 * 
 * @param config Distribution configuration
 * @returns Array of network names for each slot index
 */
export function distributeSlots(config: DistributionConfig): AffiliateNetwork[] {
  const { primary, secondary, totalSlots } = config;
  
  if (totalSlots <= 0) return [];
  
  // Filter to only configured networks
  const configuredSecondary = secondary.filter(n => n.isConfigured);
  
  // If no secondary networks configured, all slots go to primary
  if (configuredSecondary.length === 0) {
    return Array(totalSlots).fill(primary.name);
  }
  
  // Calculate total weight
  const totalWeight = primary.weight + configuredSecondary.reduce((sum, n) => sum + n.weight, 0);
  
  // Build weighted sequence that repeats
  // For 2:1 ratio → sequence is [primary, primary, secondary]
  const sequence: AffiliateNetwork[] = [];
  
  // Add primary slots based on weight
  const primaryCount = Math.round((primary.weight / totalWeight) * (primary.weight + configuredSecondary[0].weight));
  for (let i = 0; i < primaryCount; i++) {
    sequence.push(primary.name);
  }
  
  // Add secondary slots (interleaved for fairness on small counts)
  // For mobile (2 slots), we want [primary, secondary] not [primary, primary]
  for (const network of configuredSecondary) {
    const count = Math.max(1, Math.round((network.weight / totalWeight) * (primary.weight + network.weight)));
    for (let i = 0; i < count; i++) {
      sequence.push(network.name);
    }
  }
  
  // Interleave for better mobile experience
  // Simple approach: alternate primary and secondary, with primary getting extras
  const result: AffiliateNetwork[] = [];
  let primaryUsed = 0;
  let secondaryUsed = 0;
  const secondaryNetwork = configuredSecondary[0].name;
  
  // Target ratio: for every (primary.weight + secondary.weight) slots,
  // primary gets primary.weight, secondary gets secondary.weight
  const primaryWeight = primary.weight;
  const secondaryWeight = configuredSecondary[0]?.weight || 0;
  const cycleLength = primaryWeight + secondaryWeight;
  
  for (let i = 0; i < totalSlots; i++) {
    // Position within cycle
    const posInCycle = i % cycleLength;
    
    // Interleaved pattern: secondary appears at positions that give good mobile coverage
    // For 2:1 ratio (cycle=3): positions 0,2 = primary, position 1 = secondary
    // This ensures mobile (2 slots) sees [primary, secondary]
    if (secondaryWeight > 0 && posInCycle === 1) {
      result.push(secondaryNetwork);
      secondaryUsed++;
    } else {
      result.push(primary.name);
      primaryUsed++;
    }
  }
  
  return result;
}

/**
 * Get network configuration for a specific slot index.
 * Convenience function that calls distributeSlots internally.
 */
export function getNetworkForSlot(
  slotIndex: number,
  totalSlots: number,
  primary: NetworkConfig,
  secondary: NetworkConfig[]
): NetworkConfig {
  const distribution = distributeSlots({
    primary,
    secondary,
    totalSlots,
  });
  
  const networkName = distribution[slotIndex] || primary.name;
  
  if (networkName === primary.name) {
    return primary;
  }
  
  return secondary.find(n => n.name === networkName) || primary;
}

/**
 * Default network configurations
 */
export const DEFAULT_NETWORKS = {
  amazon: {
    name: 'amazon' as const,
    site: 'amazon.com',
    commission: 0.04,
    weight: 2, // 2x weight = more slots
    isConfigured: true, // Always available
  },
  iherb: {
    name: 'iherb' as const,
    site: 'iherb.com',
    commission: 0.05,
    weight: 1,
    isConfigured: false, // Needs VITE_IHERB_AFFILIATE_CODE
  },
  coupang: {
    name: 'coupang' as const,
    site: 'coupang.com',
    commission: 0.03,
    weight: 2,
    isConfigured: false, // Needs VITE_COUPANG_PARTNER_ID
  },
  yesstyle: {
    name: 'yesstyle' as const,
    site: 'yesstyle.com',
    commission: 0.10,
    weight: 1,
    isConfigured: false, // Needs VITE_YESSTYLE_AFFILIATE_ID
  },
};

/**
 * Build affiliate URL with tracking parameters.
 * @param amazonDomain Optional Amazon domain override (e.g. 'amazon.com.br'). Defaults to 'amazon.com'.
 * @param placement Optional placement context for UTM content param (e.g. 'chat', 'editor-pick').
 */
export function buildAffiliateUrl(
  originalUrl: string,
  network: AffiliateNetwork,
  affiliateCodes: {
    amazon?: string;
    iherb?: string;
    coupang?: string;
    yesstyle?: string;
  },
  amazonDomain?: string,
  placement?: string,
): string {
  const code = affiliateCodes[network];
  if (!code) return originalUrl;

  /** Append UTM query params to an already-built affiliate URL, avoiding duplicates */
  const appendUtm = (url: string): string => {
    try {
      const parsed = new URL(url);
      if (!parsed.searchParams.has('utm_source')) parsed.searchParams.set('utm_source', 'heseda');
      if (!parsed.searchParams.has('utm_medium')) parsed.searchParams.set('utm_medium', 'affiliate');
      if (!parsed.searchParams.has('utm_campaign')) parsed.searchParams.set('utm_campaign', network);
      if (placement && !parsed.searchParams.has('utm_content')) parsed.searchParams.set('utm_content', placement);
      return parsed.toString();
    } catch {
      // URL parse failed — append naively
      const sep = url.includes('?') ? '&' : '?';
      const content = placement ? `&utm_content=${encodeURIComponent(placement)}` : '';
      return `${url}${sep}utm_source=heseda&utm_medium=affiliate&utm_campaign=${network}${content}`;
    }
  };
  
  switch (network) {
    case 'amazon': {
      // Detect Amazon domain from original URL, fallback to override or default
      const urlDomainMatch = originalUrl.match(/amazon\.(com\.br|com\.mx|com\.au|co\.uk|co\.jp|com|de|fr|ca|in|it|es|sg)/i);
      const domain = urlDomainMatch ? `amazon.${urlDomainMatch[1]}` : (amazonDomain || 'amazon.com');
      // Extract ASIN and build clean URL
      const asinMatch = originalUrl.match(/\/dp\/([A-Z0-9]{10})/i)
        || originalUrl.match(/\/gp\/product\/([A-Z0-9]{10})/i);
      const asin = asinMatch?.[1]?.toUpperCase();
      
      if (asin) {
        return appendUtm(`https://www.${domain}/dp/${asin}?tag=${code}`);
      }
      const separator = originalUrl.includes('?') ? '&' : '?';
      return appendUtm(`${originalUrl}${separator}tag=${code}`);
    }
    
    case 'iherb': {
      // Remove existing rcode and add new one
      let cleanUrl = originalUrl.replace(/[?&]rcode=[^&]*/gi, '').replace(/[?&]$/, '');
      const separator = cleanUrl.includes('?') ? '&' : '?';
      return appendUtm(`${cleanUrl}${separator}rcode=${code}`);
    }
    
    case 'coupang': {
      // Extract product ID if possible
      const productMatch = originalUrl.match(/\/vp\/products\/(\d+)/)
        || originalUrl.match(/itemId=(\d+)/);
      const productId = productMatch?.[1];
      
      if (productId) {
        return appendUtm(`https://link.coupang.com/a/${code}?itemId=${productId}&lptag=AF${code}`);
      }
      return appendUtm(`https://link.coupang.com/a/${code}?url=${encodeURIComponent(originalUrl)}&lptag=AF${code}`);
    }
    
    case 'yesstyle': {
      let cleanUrl = originalUrl.replace(/[?&]aid=[^&]*/gi, '').replace(/[?&]$/, '');
      const separator = cleanUrl.includes('?') ? '&' : '?';
      return appendUtm(`${cleanUrl}${separator}aid=${code}`);
    }
    
    default:
      return originalUrl;
  }
}

/**
 * Extract product ID from affiliate URL
 */
export function extractProductId(url: string, network: AffiliateNetwork): string | null {
  switch (network) {
    case 'amazon': {
      const match = url.match(/\/dp\/([A-Z0-9]{10})/i)
        || url.match(/\/gp\/product\/([A-Z0-9]{10})/i);
      return match?.[1]?.toUpperCase() || null;
    }
    
    case 'iherb': {
      const match = url.match(/\/pr\/[^/]+\/(\d+)/i)
        || url.match(/iherb\.com\/.*?(\d{5,})/i);
      return match?.[1] || null;
    }
    
    case 'coupang': {
      const match = url.match(/\/vp\/products\/(\d+)/)
        || url.match(/itemId=(\d+)/);
      return match?.[1] || null;
    }
    
    case 'yesstyle': {
      const match = url.match(/-(\d+)\.html/i)
        || url.match(/[?&]pid=(\d+)/i);
      return match?.[1] || null;
    }
    
    default:
      return null;
  }
}
